<script setup lang="ts">
import { ref, watch, computed } from 'vue'
import { Listbox, ListboxButton, ListboxOptions, ListboxOption } from '@headlessui/vue'
import {allQuoteCurrencies, converterCurrencies, getSignFromQuote} from "@consts";
import {formatCurrency, formatPercentage} from "@/lib/formatCurrencyNumbers.ts";
import { sortedCryptoNames } from "@lib/external-data/sortedCryptoNames.ts";

const props = defineProps({
  crypto: {
    type: String,
    required: true
  },
  cryptoName: {
    type: String,
    required: true
  },
  quote: {
    type: String,
    required: true
  },
  quoteName: {
    type: String,
    required: true
  },
  coinPrice: {
    type: Number,
    required: false,
    default: undefined
  },
  coinPercentage: {
    type: Number,
    required: false,
    default: undefined
  },
  coinIcon: {
    type: String,
    required: true
  }
});

const selectedBaseCurrency = ref(props.crypto);
const selectedQuoteCurrency = ref(props.quote);
const selectedCurrencySign = getSignFromQuote(selectedQuoteCurrency.value);

const allCryptos = sortedCryptoNames;

const search = ref('')
const isBaseCurrencyOpen = ref(false)

const filteredCryptos = computed(() => {
  if (search.value === '') return allCryptos;
  const searchLower = search.value.toLowerCase();
  return allCryptos.filter(crypto => crypto.toLowerCase().includes(searchLower));
});

const toConverter = (base: string, quote: string) => {
  // TODO: add locale of current lang
  return `/converter/${base}-to-${quote}`.toLowerCase();
};

const goToConverter = (base: string, quote: string) => {
  window.location.href = toConverter(base, quote);
}

watch(selectedBaseCurrency, (newBase) => {
  goToConverter(newBase, selectedQuoteCurrency.value);
});

watch(selectedQuoteCurrency, (newQuote) => {
  goToConverter(selectedBaseCurrency.value, newQuote);
});

const handleSearch = (event: Event) => {
  search.value = (event.target as HTMLInputElement).value;
  isBaseCurrencyOpen.value = true;
}

const selectCrypto = (crypto: string) => {
  selectedBaseCurrency.value = crypto;
  isBaseCurrencyOpen.value = false;
  search.value = '';
}
</script>

<template>
  <div class="coin-info-row grid grid-cols-1 sm:grid-cols-4 gap-4 sm:gap-2 p-4 border-2 border-brand-stroke/10 rounded-4 bg-brand-background/90 relative">
    <div class="coin-logo flex items-center justify-start sm:col-span-1">
      <img
          :src="coinIcon.replaceAll('32','128')"
          :alt="`${selectedBaseCurrency}Logo`"
          class="w-15 h-auto mr-3 sm:mr-1"
      >
      <div class="coin-names flex flex-col relative">
        <input
            :value="search"
            placeholder="Search cryptocurrencies"
            class="w-full p-2 border border-brand-stroke/10 rounded-4 mb-2"
            @input="handleSearch"
            @focus="isBaseCurrencyOpen = true"
        >
        <div class="relative">
          <button
              @click="isBaseCurrencyOpen = !isBaseCurrencyOpen"
              class="w-full border border-brand-stroke/10 rounded-4 p-2 flex justify-between items-center"
          >
            <span>{{ selectedBaseCurrency }}</span>
            <span class="i-mdi:keyboard-arrow-down"></span>
          </button>
          <ul
              v-if="isBaseCurrencyOpen"
              class="absolute w-full mt-1 overflow-auto rounded-4 bg-brand-background border border-brand-stroke/10 max-h-60 z-10"
          >
            <li
                v-for="crypto in filteredCryptos"
                :key="crypto"
                @click="selectCrypto(crypto)"
                :class="[
                'cursor-pointer select-none relative py-2 pl-3 pr-9',
                crypto === selectedBaseCurrency ? 'text-brand-primary bg-brand-stroke/10' : 'text-brand-secondary'
              ]"
            >
              <span :class="['block', crypto === selectedBaseCurrency && 'font-semibold']">
                {{ crypto }}
              </span>
              <span
                  v-if="crypto === selectedBaseCurrency"
                  class="absolute inset-y-0 right-0 flex items-center pr-4 text-brand-primary"
              >
                <span class="i-ep:select w-5 h-5"></span>
              </span>
            </li>
          </ul>
        </div>
        <span class="coin-long-name text-sm text-brand-secondary">{{ cryptoName }}</span>
      </div>
    </div>
    <div class="coin-info flex flex-col sm:col-span-2 justify-center">
      <div class="coin-price flex flex-col items-center align-middle">
        <span class="price-value text-4xl font-bold text-brand-primary">{{ formatCurrency(coinPrice, selectedCurrencySign) }}</span>
        <span
            class="price-label text-xs text-brand-secondary"
            :class="{'text-brand-primary': (coinPercentage??0) >= 0, 'text-brand-danger': (coinPercentage??0) < 0}"
        > {{ $t('up_of') }} {{ formatPercentage(coinPercentage) }} {{ $t('respect_yesterday') }}</span>
      </div>
    </div>
    <div class="coin-change sm:col-span-1">
      <Listbox v-model="selectedQuoteCurrency">
        <div class="relative">
          <ListboxButton class="w-full border border-brand-stroke/10 rounded-4 p-2 flex justify-between items-center">
            <span>{{ selectedQuoteCurrency }}</span>
            <span class="i-mdi:keyboard-arrow-down"></span>
          </ListboxButton>
          <ListboxOptions class="absolute w-full mt-1 overflow-auto rounded-4 bg-brand-background border border-brand-stroke/10 max-h-60 z-10">
            <ListboxOption
                v-for="cur in allQuoteCurrencies"
                :key="cur"
                :value="cur"
                v-slot="{ active, selected }"
                as="template"
            >
              <li :class="[
                'cursor-pointer select-none relative py-2 pl-3 pr-9',
                active ? 'text-brand-primary bg-brand-stroke/10' : 'text-brand-secondary'
              ]">
                <span :class="['block truncate', selected && 'font-semibold']">
                  {{ cur }}
                </span>
                <span
                    v-if="selected"
                    :class="[
                    'absolute inset-y-0 right-0 flex items-center pr-4',
                    active ? 'text-brand-primary' : 'text-brand-secondary'
                  ]"
                >
                  <span class="i-ep:select w-5 h-5"></span>
                </span>
              </li>
            </ListboxOption>
          </ListboxOptions>
        </div>
      </Listbox>
      <span class="price-label text-xs text-brand-secondary mt-1">
        {{ quoteName }}
      </span>
    </div>
  </div>
</template>

<style scoped>
.change-chip {
  display: inline-block;
  padding: 0.2rem 0.5rem;
}

.w-90-perc {
  width: 90%;
}
</style>